// @refresh reset — dev-only: force remount so board nodes hot-reload on edit
import { useMemo } from "react";
import { type Node, type NodeTypes } from "@xyflow/react";
import { BoardCanvas } from "./BoardCanvas";
import { SectionBox, SectionGrid } from "./SectionBox";
import { PresentationFrame } from "./PresentationFrame";
import { DocumentFrame } from "./DocumentFrame";
import { ProjectFrame } from "./ProjectFrame";
import { PlatformArchitecture } from "./falcon/PlatformArchitecture";
import { FalconSessionRoll } from "./falcon/session-roll";
import { SiteWall } from "./falcon/SiteWall";
import { MeetingTwo } from "./falcon/MeetingTwo";
import { EmailTemplate } from "./falcon/EmailTemplate";
import { TicketsKanbanPage } from "../tickets/TicketsKanbanPage";

// ─────────────────────────────────────────────────────────────────────────────
// THE FALCON BOARD — engagement surface for Falcon Real Estate (פלקון נדל״ן).
//
// Dan Shika is INDEPENDENT — no partner, no firm. (An earlier pass recorded a
// partnership with a "Daniel Gaminer"; that person appears nowhere in the
// meeting transcript. Corrected 2026-07-25. He was at Pisgot and is on his own
// now, which is why he cannot run a twenty-process pipeline.)
//
// THREE boxes, on purpose (Erez, 2026-07-25 — "if you READ it, it is a document;
// if you SCAN it, it stays a box"):
//   Discovery  — the substance, as ONE <PresentationFrame>: the narrated film
//               and the click-through slides are two VIEWS of the same deck
//               (pitch-doc/consultancy/falcon/first-meeting), plus the PDF.
//               Built code-first from that page_content row (skill://remotion),
//               never screen-captured, so copy and timing cannot drift.
//   Diagrams   — live React SVG. The one thing a scrolling page does badly.
//   Decisions  — live state: what is blocking right now, at a glance.
//
// History: twelve card boxes of prose → four documents → one presentation.
// Editing the deck row updates the deck and (after a re-render) the film + PDF.
//
// Board standard: BoardCanvas + SectionBox/SectionGrid, one grid node, wide grid.
// Jarvis, 2026-07-23 · restructured 2026-07-25. Owner agent: Otto.
// ─────────────────────────────────────────────────────────────────────────────

const T = {
  bg: "#E7E5E1", dots: "#D2CFC9",
  ink: "#1B211E", ink2: "#4A534D", faint: "#8C948E",
  line: "#ECE7DF", hair: "#E3DED4",
  card: "#FFFFFF", paper: "#FAF7F2", bone: "#F2EDE4",
  green: "#3F8F6F", greenDeep: "#2E6B52", greenWash: "#EEF5F1",
  plum: "#7A5B78", amber: "#B07A1E", red: "#C0492F", blue: "#2F6BB0", petrol: "#2C7E9E",
};
const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";
const EYEBROW: React.CSSProperties = {
  fontFamily: MONO, fontSize: 12, fontWeight: 600, letterSpacing: "0.14em",
  textTransform: "uppercase", color: T.faint,
};

type CardData = { title: string; eyebrow?: string; body?: string; bullets?: string[]; accent?: string; chip?: string; chipTone?: string; chips?: { label: string; tone: string }[] };

function Chip({ label, tone }: { label: string; tone: string }) {
  return (
    <span style={{ display: "inline-block", fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#fff", background: tone, borderRadius: 999, padding: "4px 12px", whiteSpace: "nowrap" }}>{label}</span>
  );
}

function Card({ d, width = 460 }: { d: CardData; width?: number }) {
  return (
    <div data-zoom style={{ width, boxSizing: "border-box", background: T.card, border: `1px solid ${T.hair}`, borderTop: `4px solid ${d.accent || T.green}`, borderRadius: 18, boxShadow: "0 10px 30px rgba(27,33,30,0.05)", padding: 26, fontFamily: FONT, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          {d.eyebrow && <div style={{ ...EYEBROW, color: d.accent || T.green, marginBottom: 6 }}>{d.eyebrow}</div>}
          <div style={{ fontSize: 21, fontWeight: 600, color: T.ink, letterSpacing: "-0.01em", lineHeight: 1.2 }}>{d.title}</div>
        </div>
        {/* `chips` (plural) is the WhatsApp log's tag row; `chip` (singular) is
            the older one-tag form every other section on this board still uses.
            Both render, neither replaces the other — changing the single form
            would have touched six sections to serve one. */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "flex-end" }}>
          {d.chip && <Chip label={d.chip} tone={d.chipTone || T.green} />}
          {d.chips?.map((c, i) => <Chip key={i} label={c.label} tone={c.tone} />)}
        </div>
      </div>
      {d.body && <div style={{ fontSize: 15, lineHeight: 1.55, color: T.ink2 }} dangerouslySetInnerHTML={{ __html: d.body }} />}
      {d.bullets && (
        <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
          {d.bullets.map((b, i) => (
            <li key={i} style={{ fontSize: 14.5, lineHeight: 1.5, color: T.ink2, display: "flex", gap: 10 }}>
              <span style={{ color: d.accent || T.green, fontWeight: 700, flexShrink: 0 }}>·</span>
              <span dangerouslySetInnerHTML={{ __html: b }} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Grid({ cards, cols = 2, width = 460 }: { cards: CardData[]; cols?: number; width?: number }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, ${width}px)`, gap: 26 }}>
      {cards.map((c, i) => <Card key={i} d={c} width={width} />)}
    </div>
  );
}

// One day of the WhatsApp thread: a date heading and that day's messages under
// it. Several of these sit side by side inside ONE "WhatsApp" box so the dates
// read across like a calendar rather than down like a transcript — Erez's
// shape, 20 Aug. A day is a COLUMN, so its cards stack; the box grows sideways
// as more days arrive, which is also how the next retrieval will be added.
function WaDay({ label, cards, width = 520 }: { label: string; cards: CardData[]; width?: number }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, paddingBottom: 12, borderBottom: `2px solid ${T.hair}` }}>
        <div style={{ fontFamily: FONT, fontSize: 26, fontWeight: 600, color: T.ink, letterSpacing: "-0.01em" }}>{label}</div>
        <div style={{ ...EYEBROW, fontSize: 11 }}>{cards.length} messages</div>
      </div>
      {cards.map((c, i) => <Card key={i} d={c} width={width} />)}
    </div>
  );
}

// ── WHATSAPP LOG — one card per message, for EYEBALLING against WhatsApp ─────
//
// ⛔ THIS IS A COMPLETENESS CHECK, NOT A SUMMARY. Erez's instruction, 20 Aug:
// build the cards FIRST, before tickets and before analysis, so he can open
// WhatsApp beside the board and confirm with his own eyes that every message is
// here. We had never done that check. So the rule for this section is the
// opposite of every other section on this board: NOTHING IS CONDENSED, nothing
// is judged worth leaving out, and a message with no product content still gets
// a card — because a missing card is indistinguishable from a message that was
// never sent, and that is exactly what he is checking for.
//
// Tags carry the metadata so the card body can stay the message itself:
// who sent it, what kind it is, and whether it is fully recovered.
const WA_TAG = {
  dan:   { label: "Dan", tone: T.plum },
  erez:  { label: "Erez", tone: T.blue },
  voice: { label: "Voice note", tone: T.petrol },
  text:  { label: "Text", tone: T.faint },
  spec:  { label: "Requirement", tone: T.greenDeep },
  bug:   { label: "Bug report", tone: T.red },
  social:{ label: "Social", tone: T.faint },
  gap:   { label: "NOT RECOVERED", tone: T.amber },
};

const WA_19: CardData[] = [
  { eyebrow: "19:03", title: "The property list is hiding most of his inventory",
    accent: T.red, chips: [WA_TAG.dan, WA_TAG.text, WA_TAG.bug],
    body: "Pasted from Falcon itself: in the general property list he sees only about <b>20–30 properties</b> though the database holds about 90. Opening a specific owner's card (example: Switchup) shows further properties belonging to that owner that <b>do not appear in the ordinary list</b>.<br><br>His request: every property under an owner must also appear in the general list. <i>“יש חשד לבאג בפילטר/הצגה”</i> — he suspects a filter or display bug." },
  { eyebrow: "19:08", title: "“Excellent. I'll sort it out.”",
    accent: T.blue, chips: [WA_TAG.erez, WA_TAG.text],
    body: "<i>מעולה / אסדר את זה</i> — the first of two promises of an update." },
  { eyebrow: "19:10 · 0:35", title: "Ten properties a day, and what that compounds to",
    accent: T.plum, chips: [WA_TAG.dan, WA_TAG.voice],
    body: "“By the way, I'm continuing to upload properties in the meantime — it's great, it's amazing output.<br><br>It's enough that I upload <b>ten properties a day</b> from WhatsApp groups. On a day when I work more I can upload twenty, thirty — sometimes there are lists. But even modestly, five to ten a day, then think about the progress.<br><br>By September I could have a really serious list of relevant properties.”<br><br><i>The remainder is personal — asking whether Erez is still out, and mentioning he'll drop by with his friend Gil Stein around ten.</i>" },
  { eyebrow: "20:05 · 0:22", title: "Erez's own voice note, plus a 🚀",
    accent: T.amber, chips: [WA_TAG.erez, WA_TAG.voice, WA_TAG.gap],
    body: "<b>Not transcribed.</b> It is Erez's side of the conversation, so it was skipped during retrieval. Listed here rather than omitted, because the point of this section is that you can see everything that exists — including what we chose not to open." },
  { eyebrow: "20:59", title: "127 properties in the system, 78 added that day",
    accent: T.blue, chips: [WA_TAG.erez, WA_TAG.text],
    body: "“Total in the system: <b>127 properties</b>. Added today: <b>78 new properties</b>. Heavy work day. Rest well.”<br><br><i>Note the drift: Dan's 19:03 message says about 90. By 20:59 it is 127. A count quoted in the evening was already stale — verify against the live box before building on either figure.</i>" },
  { eyebrow: "21:01 · 0:29", title: "“500 properties by the beginning of September”",
    accent: T.plum, chips: [WA_TAG.dan, WA_TAG.voice, WA_TAG.spec],
    body: "“Think about it — if I give, say, two or three concentrated days a week, a few hours each, sitting at the computer and on my WhatsApp groups… God willing, I'm telling you, I'll reach around <b>500 properties by the beginning of September</b>. Those are the main properties, and on that I'll have all the data to work with properly.<br><br>I'll always add more, but that will give me all the core data. You understand? Amazing.”" },
  { eyebrow: "21:06", title: "“Second brain”",
    accent: T.blue, chips: [WA_TAG.erez, WA_TAG.text],
    body: "Two words. It is the clearest statement of what he thinks he is buying, and it came from Erez, not from us." },
  { eyebrow: "after 21:06", title: "The rest of the evening — personal, and NOT captured",
    accent: T.amber, chips: [WA_TAG.gap, WA_TAG.social],
    body: "Retrieval judged everything after 21:06 to be personal or social — a video, plans for the evening — and did not transcribe it. Four short clips sit in there: <b>0:09, 0:05, 0:03 and a 0:26 video</b>.<br><br><b>This card exists so the judgement is visible rather than invisible.</b> Somebody decided these did not matter; that somebody was not you. They can be pulled and transcribed in about a minute each." },
];

const WA_20: CardData[] = [
  { eyebrow: "09:17", title: "“Good morning, yesterday was fun!”",
    accent: T.plum, chips: [WA_TAG.dan, WA_TAG.text, WA_TAG.social],
    body: "<i>בוקר אור, היה כיף אתמול!</i>" },
  { eyebrow: "09:18", title: "He had Falcon write the feedback for him",
    accent: T.plum, chips: [WA_TAG.dan, WA_TAG.text],
    body: "<i>בקשתי מפלקון לנסח לך קצת משוב, עבדתי עליו גם בלילה..</i><br><br>“I asked Falcon to draft you some feedback, I worked on it during the night too.”<br><br><b>This one line is why the next card is not an independent signal.</b> The in-product requests and the WhatsApp message below are the same event relayed twice — he composed it inside the product, then pasted it here." },
  { eyebrow: "09:21", title: "The spec — three points, in his own words",
    accent: T.greenDeep, chips: [WA_TAG.dan, WA_TAG.text, WA_TAG.spec],
    body: "<b>1 · A real “אזור” field on the properties table</b> — multi-select, on his rubric:<br>סובב שרונה והקריה · מידטאון (מע\"ר צפוני) · חסן עראפה / יצחק שדה (מע\"ר דרומי), incl. מתחם הארבעה · הבורסה, רמת גן · ציר יגאל אלון / איילון · אזור הסיטי<br>…with אזור הסיטי split four ways: רוטשילד+אחד העם | יהודה הלוי+לילינבלום | הרצל+נחלת בנימין+אלנבי | המסגר+רב אשי<br>“עד אז זה בהערות ידנית, לא ניתן לסינון אמיתי.”<br><br><b>2 · A real “DNA” per property</b> — beyond free-text notes, supporting automatic Soft Matching when ranking properties for a client. “לא רק שדה טקסט שפלקון קורא ידנית.”<br><br><b>3 · Delete 10 empty records</b> from the קבוצת שמואל פקטור list (seq 74, 76–84). “אין לי כלי מחיקה.”" },
  { eyebrow: "09:31", title: "“Good morning brother, yes really cool there :)”",
    accent: T.blue, chips: [WA_TAG.erez, WA_TAG.text, WA_TAG.social] },
  { eyebrow: "09:32", title: "“Excellent feedback — I'll do an update and update you.”",
    accent: T.blue, chips: [WA_TAG.erez, WA_TAG.text],
    body: "<i>מעולה המשובים אני יעשה עדכון ואעדכן</i> — the second promise of an update. No date given, on either." },
  { eyebrow: "10:00 · 0:51", title: "The client side — areas, size bands, lead temperature",
    accent: T.greenDeep, chips: [WA_TAG.dan, WA_TAG.voice, WA_TAG.spec],
    body: "“And maybe best of all, also open up a rubric on the <b>clients</b> side.<br><br>A few rubrics for filtering — for example <b>seekers (מחפשים)</b> — and then to choose one of those five areas, where <b>you can pick two of them</b>, understand? So that you can <b>cross-reference (הצלבה)</b>. That's one idea, a good one I think.<br><br>And two: that the seekers, on the clients axis, should be <b>divided by office size: small, medium, large</b>. For the sake of argument, <b>small is up to 300, medium is up to 1000, large is 1000 and above</b>. So that I can also filter by that.<br><br>And maybe one more filter that could be set up front — that's essentially the <b>heat level of the lead</b> — so that you can also move it into a column of <b>hot leads, cold leads</b>.<br><br>And today I'm also starting to [<b>UNCLEAR</b>] clients into the list.<br><br>Alright, brother? Thank you very much.”" },
  { eyebrow: "10:00 · 46.8s", title: "⚠️ The one word nobody has confirmed",
    accent: T.amber, chips: [WA_TAG.gap],
    body: "The transcript reads <i>“מתחיל <b>למעלה</b> לקוחות לתוך הרשימה”</i>. <b>למעלה</b> is almost certainly a mis-hearing of <b>להעלות</b> — which makes it “today I'm also starting to <b>upload</b> clients into the list.”<br><br>It fits the grammar, the context, and his 19:10 note about loading records daily. It was flagged rather than smoothed, deliberately.<br><br><b>If it is right, every client record he creates today lands in a table with no area, no size band and no lead temperature — and he revisits all of them.</b> It is the difference between the client-side work being urgent and merely next. One question to Dan settles it." },
  { eyebrow: "10:02", title: "“Checked, excellent.”",
    accent: T.blue, chips: [WA_TAG.erez, WA_TAG.text],
    body: "<i>בדוק מעולה</i> — the last message in the thread." },
];

// ── VOICE ────────────────────────────────────────────────────────────────────
// The agent's Hebrew voice, playable on the board so it can be judged by ear
// here rather than in a folder of files. Erez, 2026-07-26: "make it a bit more
// echo style". The voice is not one voice — it is a BLEND of 55% am_echo and
// 45% am_santa, and Kokoro voices are style VECTORS, so two of them average
// into one that does not exist in the model. "More echo" is therefore a dial,
// not a switch, and this box walks it.
//
// The clips are the SAME Hebrew sentence — a real Falcon reply rather than a
// test phrase, because the accent only matters on the words this product
// actually says — encoded as Telegram Opus so what plays here is what Dan will
// hear, not something cleaner.
//
// Two <source>s per clip: Opus-in-Ogg is the shipped artefact, AAC is there
// because Safari only picked up Opus-in-Ogg recently and a board has to play
// for whoever opens it. Both are encoded from the same WAV, never from each
// other. Generator: agents/otto/voice-samples/voice_mixes.py.
const VOICE_BASE = "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/board/falcon/voice";

// The two roll diagrams. Static `?v=` at each use site rather than a timestamp:
// a cache-busting Date.now() re-downloads every image on every board open.
const ROLL_BASE = "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/board/falcon/roll";

type Clip = { file: string; title: string; mix: string; note: string; chip?: string; tone?: string };

const VOICE_CLIPS: Clip[] = [
  { file: "1_current_55echo", title: "55% echo", mix: "55% am_echo · 45% am_santa · speed 1.0",
    note: "Dave's original tuning, arrived at with Erez over several rounds of listening — before Erez could hear the Hebrew well enough to judge it on a real reply." },
  { file: "2_70echo", title: "70% echo", mix: "70% am_echo · 30% am_santa · speed 1.0",
    note: "One step up the dial. The santa warmth is still there, just quieter." },
  { file: "3_85echo", title: "85% echo", mix: "85% am_echo · 15% am_santa · speed 1.0",
    note: "Most of the way. A trace of the blend remains in the lower register." },
  { file: "4_pure_echo", title: "Pure echo", mix: "100% am_echo · speed 1.0",
    note: "No blend at all, normal speed. <b>Erez's pick, 26 July — this is what the bot speaks.</b> Verified on the box after deploy, not in the diff: the running process reports am_echo 1.0.",
    chip: "SHIPPING", tone: T.green },
  { file: "5_pure_echo_slower", title: "Pure echo, slowed", mix: "100% am_echo · speed 0.92",
    note: "Stripping out santa makes the delivery a touch brisker, so this put the pace back. Tried and rejected — the timbre was the thing, not the speed." },
];

function VoiceCard({ c, width = 460 }: { c: Clip; width?: number }) {
  return (
    <div data-zoom style={{ width, boxSizing: "border-box", background: T.card, border: `1px solid ${T.hair}`, borderTop: `4px solid ${c.tone || T.plum}`, borderRadius: 18, boxShadow: "0 10px 30px rgba(27,33,30,0.05)", padding: 26, fontFamily: FONT, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ ...EYEBROW, color: c.tone || T.plum, marginBottom: 6 }}>{c.mix}</div>
          <div style={{ fontSize: 21, fontWeight: 600, color: T.ink, letterSpacing: "-0.01em", lineHeight: 1.2 }}>{c.title}</div>
        </div>
        {c.chip && <Chip label={c.chip} tone={c.tone || T.plum} />}
      </div>
      {/* preload="none" on purpose: five clips on one board is five downloads
          nobody asked for until they press play. */}
      <audio controls preload="none" style={{ width: "100%", height: 40 }}>
        <source src={`${VOICE_BASE}/${c.file}.ogg`} type="audio/ogg" />
        <source src={`${VOICE_BASE}/${c.file}.m4a`} type="audio/mp4" />
      </audio>
      <div style={{ fontSize: 14.5, lineHeight: 1.5, color: T.ink2 }} dangerouslySetInnerHTML={{ __html: c.note }} />
    </div>
  );
}

// The two hand-rolled diagrams that used to live here (the vision and the
// Falcon OS map) moved INTO the architecture deck as DiagramSlides, where
// they draw themselves in narration order (Erez, 2026-07-25). Their data now
// lives in agents/otto/falcon-arch-video/scripts/content.mjs and the
// pitch-doc/consultancy/falcon/architecture row, so there is one copy, not two.

// ── BOARD-NATIVE CONTENT ONLY ────────────────────────────────────────────────
// The card walls that used to live here (business, opportunity, requirements,
// priorities, deprioritized, modules, the front, the plan, market, assets) were
// PROSE crammed into cards. They now live as real documents in page_content
// under kb-doc/consultancy/falcon/ and render on this board via <DocumentFrame>.
// The rule (Erez, 2026-07-25): if you READ it, it is a document; if you SCAN it,
// it stays a box. Only diagrams and live decisions stay board-native.
// Deleted here rather than commented out — the documents are the one source.

// What is actually RUNNING, measured on the box rather than planned. The
// gating risk of the whole engagement — Hebrew speech-to-text — closed on
// 26 July. Full detail: kb-doc/consultancy/falcon/infrastructure.
const BUILD: CardData[] = [
  { eyebrow: "26 Jul · live", title: "Dan can talk to it, and it talks back", chip: "TESTABLE", chipTone: T.green, accent: T.green,
    bullets: ["<b>@tmfalconbot</b> answers in Hebrew — a real Pydantic-AI agent on deepseek-v4-pro with five tools over the brain.", "<b>Voice OUT:</b> Kokoro + Phonikud, language decided from the reply text, no setting to forget.", "<b>Voice IN, shipped this run:</b> a note goes download → transcribe → extract → write gate → a card that <b>reads the record back</b>.", "Every write passes the gate: provenance and confidence <b>per field</b>, an append-only audit trail, and the original audio kept."] },
  { eyebrow: "The gating risk", title: "Hebrew STT — closed by turbo", chip: "SOLVED", chipTone: T.green, accent: T.green,
    bullets: ["<b>large-v3-turbo</b> is what made it viable: large-v3's encoder with a <b>four-layer decoder instead of thirty-two</b>, so it hears as well for a fraction of the decode.", "Measured on <b>Dan's own notes</b>: small 1.5× but mangled · medium 0.52× / 3.1 GB · large-v3 <b>OOM</b> on 4 GB · <b>turbo 1.24× / 2.7 GB</b>.", "<b>ivrit-ai's Hebrew-tuned turbo: same speed, same memory, clearly better Hebrew</b> — and it is the default. One env var flips back.", "<b>Still to prove:</b> accuracy on a real street recording. Clean audio proves the pipeline, not the ear."] },
  { eyebrow: "26 Jul · the box", title: "Resized in place — 4 cores, 8 GB", chip: "DONE", chipTone: T.green, accent: T.green,
    bullets: ["<b>falcon-agent</b> · Hetzner <b>CX33</b>, 4 vCPU / 8 GB, Falkenstein. IP and disk survived the resize.", "<b>The CPU doubling mattered as much as the RAM</b> — transcription was speed-bound, not only memory-bound.", "€5.99 → €8.99/month. <b>I had told Erez +$2.50; the real delta is ~+$3.50</b> — corrected.", "Postgres in Docker, bot as a systemd unit, long polling (a webhook needs a domain we do not have yet)."] },
  { eyebrow: "Found by running it", title: "Three bugs reasoning would not have caught", chip: "FIXED", chipTone: T.amber, accent: T.amber,
    bullets: ["<b>A reasoning model returns an EMPTY STRING, not an error.</b> deepseek-v4-pro bills its thinking from the same max_tokens budget; run out mid-thought and it sends HTTP 200 with no content. The extractor read that as <i>\"no property in this note\"</i>. <b>Every direct caller on the old 400-token default was silently exposed.</b>", "<b>Not every voice note is a property.</b> One of Dan's real notes is him talking to a client about scheduling — we filed an empty row for it. An empty row is worse than no row when the brain <b>is</b> the product.", "<b>Identity fields cannot arrive confirmed from audio.</b> Whisper's confidence does not track its accuracy in Hebrew — a note scoring \"very clear\" was nonsense. Building, address, floor and owner are held below the gate's floor so the agent always <b>asks</b>."] },
  { eyebrow: "The architecture", title: "Keep the spine, replace the organs", accent: T.petrol,
    bullets: ["Own repo on its own box — <b>forked</b>, because Boiling Offers is a live client system and every shared-spine change would be a change to someone else's running product.", "Honest reuse after stripping the affiliate third: <b>~40%, not the 62% first reported</b> — autonomy and talk_change were coupled too. Backend 4,461 → 1,771 lines.", "What we kept is the hard-to-get-right part: RLS tenancy (proven on the box, not assumed), the outbound guardrail gate, the agent runtime.", "<b>No open-weights lock</b> — Falcon picks the model on merit."] },
  { eyebrow: "Overdue", title: "Housekeeping we owe", chip: "OPEN", chipTone: T.amber, accent: T.amber,
    bullets: ["<b>Backups are still not configured</b> — which flatly contradicts \"the data is the product\".", "Code reaches the box by <b>rsync, not git</b>. Fine for a prototype, not for a client system.", "No domain yet, so the bot long-polls instead of taking a webhook."] },
];

const DIRECTION: CardData[] = [
  { eyebrow: "Erez · 25 Jul", title: "Build everything. Telegram is the face.", chip: "SETTLED", chipTone: T.green, accent: T.green,
    bullets: ["<b>\"We're going to build a CRM that has the workflow and the agent, just like we did for Boiling Offers — then we change it for these use cases.\"</b> So the answer to the scope question is: all of it, on the proven spine.", "<b>Telegram, not WhatsApp.</b> Settled. It is where the agent lives and where Dan talks to it.", "<b>The flows come first and they guide the build</b> — the screens are drawn from the conversations, not the other way round."] },
  { eyebrow: "Correction · 25 Jul", title: "Dan is independent — there is no partner", accent: T.red,
    bullets: ["An earlier pass recorded a partnership with a \"Daniel Gaminer\". <b>That person appears nowhere in the discovery transcript</b> — zero occurrences, checked directly.", "Dan was at <b>Pisgot</b> and is on his own now, which is exactly why he cannot run a twenty-process pipeline and wants to work only on led processes.", "Corrected in the project brain. <b>One broker, one tenant</b> — the tenant question is closed."] },
];

const DECISIONS: CardData[] = [
  { eyebrow: "For Erez · 1", title: "Inventory capture — the one thing Telegram does not solve", chip: "OPEN", chipTone: T.amber, accent: T.amber,
    bullets: ["Telegram settles where Dan <b>talks to</b> the agent. It does not settle where his <b>inventory comes from</b> — that is WhatsApp broker groups, and it always has been.", "<b>Flow 2 shows the honest day-one answer:</b> he forwards or pastes the group message and the agent parses it. That works immediately, costs nothing, and needs no Meta approval.", "The alternative — a real WhatsApp group bridge — is a materially bigger build and the official API does not support group ingestion.", "<b>Rec:</b> ship forward-and-parse, measure how often he actually does it, and only then decide if a bridge is worth it."] },
  { eyebrow: "For Erez · 2", title: "Hebrew STT is built — now it needs a real note", chip: "YOUR TEST", chipTone: T.green, accent: T.green,
    bullets: ["<b>The gating risk is closed as a build.</b> Half the flows on this board open with a Hebrew voice note, and that path now runs end to end on the box.", "<b>What is proven:</b> the pipeline — transcript → extraction → per-field confidence → the gate → a card that reads the record back. Verified on a clean dictation: eleven fields, full audit trail.", "<b>What is NOT proven:</b> accuracy on a noisy street. Clean audio tests the plumbing, not the ear, and only Dan's real recordings settle it.", "<b>Ask:</b> send @tmfalconbot a Hebrew note describing a property as if you had just walked out of it. That is the test that counts."] },
  { eyebrow: "For Erez · 3", title: "Commercials + who owns the dataset", accent: T.plum,
    bullets: ["The moat we sold is <b>the data</b>, and it will sit on our server, about his clients and their landlords.", "<b>Say who owns it before we start accumulating it</b>, not after. It also feeds Requirement 5 — his information-protection dilemma.", "<b>Rec:</b> he owns his records, we own the platform.", "Still outstanding: pricing, <b>Dan's email address</b>, and whether I go back to Dan directly or it routes through you."] },
  { eyebrow: "Confirm with Dan", title: "Open questions", bullets: ["Exact commission model (leasing / sales)?", "Does he keep any spreadsheet today, or is it genuinely all in his head?", "Brand direction for \"Falcon\" — the logo and signature he asked for.", "Which of the 12 flows would he want first?"], accent: T.amber },
];

/** A forced row break inside <SectionGrid>'s flex-wrap. Zero height, full
 *  width — so the next box starts a new line regardless of how much room is
 *  left. The Marketing board uses the same device to pin its rows. */
const Break = () => <div style={{ flexBasis: "100%", height: 0 }} />;

function GridNode() {
  return (
    // Widened past 6200 for the site wall: ten full homepages at 900px each on
    // two rows is genuinely wide, and the grid must not wrap them into a column.
    <SectionGrid width={20000} gap={190}>
      {/* ACTION ITEMS — FIRST, because it is the only section that answers
          "what are we doing next" (Erez, 2026-08-11). Everything below is
          reference: what we learned, what we shipped, what we showed Dan.
          This is the one you act on.

          It is the same /tickets Kanban, not a Falcon component:
          <TicketsKanbanPage project="…" embedded /> is the ONE app, and any
          other project board gets its own slice by passing a different slug.
          Clicking a card opens the ticket BESIDE the columns rather than
          navigating away — on a board there is nowhere to navigate to without
          losing the canvas. Drag persists through the same PUT the full page
          uses, so a card moved here is moved everywhere. */}
      <SectionBox title="Action items" layout="custom">
        <div style={{ marginTop: 0 }}>
          <TicketsKanbanPage project="falcon-real-estate" embedded />
        </div>
      </SectionBox>
      <Break />
      {/* ONE presentation per topic (Erez-locked 2026-07-25). A <DeckFrame> of
          the same slug used to sit above this — two boxes showing one deck. The
          film view already IS the deck, narrated, so the DeckFrame was deleted
          rather than kept "just in case". Name the box for the TOPIC (Discovery),
          not for the medium ("The Film") — the medium is a toggle inside it. */}
      {/* CURRENT STATE, added by Atlas 2026-08-03. Falcon is the first
          Tailormind cloud application now, and most of what sits below this row
          describes the JULY build — the Pydantic agent on deepseek, the
          "keep the spine, replace the organs" repo, the three-phase plan. All
          superseded, all kept: a board is the record and nothing comes off it
          without Erez. This box is the honest front door, and it follows the
          board law — written in the KB, presented here.
          NOTE FOR OTTO: the "Build state" cards below describe the RETIRED
          stack. Flagged rather than removed. */}
      {/* CLEANED 15 Aug, section by section, on Erez's explicit instruction —
          and then his correction: ONE section called "Project docs", four pages
          side by side, not two sections split by kind.

          Four sections went first: "What we can find ourselves" (the
          data-sources survey, asked and answered), "The Tailormind standard"
          (three pages that were OUR retelling of the platform) and the two
          "Session memory" design boards (six pages exploring a design that
          shipped on 13 Aug).

          His reasoning: the architecture already has TWO canonical documents —
          Tailormind Core and Tailormind Falcon — so anything true about this box
          belongs in one of those, not in a Falcon-local retelling that drifts
          from them. They are presented here now, so the board points at the
          source instead of paraphrasing it.

          The four, in his order: where the project stands · what it must do ·
          the contract every box is built against · this instance. Every removed
          KB row still exists at its slug — off the board, not out of the brain. */}
      <SectionBox title="Project docs" layout="custom">
        <div style={{ marginTop: 0, display: "flex", gap: 24 }}>
          {/* THE ROW, not a page. Erez's call, 15 Aug: the `projects` row is the
              truth every agent maintains, the kb-doc/projects/* pages were a
              parallel copy nobody updated — Falcon's still said "nothing
              deployed" six weeks after go-live — so the pages go and the board
              reads the row through the same mdToBlocks converter the project
              detail page uses. Nothing here can drift, because nothing here is
              a copy. */}
          <ProjectFrame
            slug="falcon-real-estate"
            label="Project summary — where the engagement stands"
            width={880}
            height={1240}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/falcon/brd"
            label="BRD — what Falcon must do for Dan"
            width={880}
            height={1240}
          />
          <DocumentFrame
            slug="kb-doc/myjarvis-os/architecture/tailormind-core"
            label="Tailormind Core — the contract every client box is built against"
            width={880}
            height={1240}
          />
          <DocumentFrame
            slug="kb-doc/myjarvis-os/architecture/tailormind-falcon"
            label="Tailormind Falcon — this instance, its divergences and open questions"
            width={880}
            height={1240}
          />
        </div>
      </SectionBox>
      <Break />
      {/* WHAT DAN SAYS WHEN HE IS NOT IN A MEETING (20 Aug 2026).
          Erez asked for a WhatsApp retrieval section, and this is it.

          ⛔ THE MECHANISM MATTERS, because it is the only reason this section
          can exist. I have no WhatsApp access and never will — this box has no
          logged-in Chrome and no residential IP. Jarvis Local sits on Erez's
          Mac, reads the thread there, transcribes the voice notes locally, and
          writes the result to the KB. I ask via
          `.claude/scripts/ask-jarvis.sh` and name the contact myself, because
          he owns the machine and I own the client.

          Reading is help, sending is not. Nobody here replies to Dan.

          It sits directly under Project docs because it is the CLIENT'S OWN
          WORDS, and everything below this line is our account of the work. When
          the two disagree, this wins.

          Future retrievals land as sibling pages under
          kb-doc/consultancy/falcon/whatsapp/, so this box grows a card per
          thread rather than being rewritten. */}
      {/* ⛔ THE CARDS COME BEFORE THE DOCUMENTS, AND BEFORE ANY TICKET.
          Erez, 20 Aug: build the cards first, he validates them against
          WhatsApp with his own eyes, and only then do we discuss or cut
          tickets. We had never run that check on a retrieval.

          One card per message, both directions, in time order, nothing
          condensed and nothing dropped for being unimportant — a social
          "good morning" gets a card, because a missing card and a message
          that was never sent look identical, and telling those apart is the
          entire purpose of this section.

          Amber tags mark what is NOT recovered. There are three: Erez's own
          voice note at 20:05, the four short clips after 21:06 that retrieval
          judged personal, and the one unclear word at 46.8s. Those are the
          cards most worth his eyes, because each one is somebody's judgement
          rather than a fact. */}
      <SectionBox title="WhatsApp" layout="custom">
        {/* ONE BOX, DAYS ACROSS. Erez, 20 Aug: the section is called WhatsApp
            and nothing else — the date is a subtitle inside it, not part of the
            title — so the days sit next to each other and read like a calendar.
            `align-items: flex-start` so a short day does not stretch to match a
            long one; each column is its own height. */}
        <div style={{ marginTop: 0, display: "flex", gap: 56, alignItems: "flex-start" }}>
          <WaDay label="August 19th" cards={WA_19} />
          <WaDay label="August 20th" cards={WA_20} />
        </div>
      </SectionBox>
      <Break />
      {/* RENAMED, not removed (20 Aug). "WhatsApp" is now the cards box above —
          Erez's title, and the view he actually reads. Two adjacent sections
          both called WhatsApp is a worse problem than either name, so this one
          takes the name of what it holds: the retrieval the cards were built
          from, and my reading of it. */}
      <SectionBox title="Source & analysis" layout="custom">
        <div style={{ marginTop: 0, display: "flex", gap: 24 }}>
          {/* TWO FRAMES, AND THE ORDER IS THE ARGUMENT. His words first, my
              reading second — never the other way round, and never merged into
              one page. Retrieval hands over MATERIAL; the judgement is mine and
              it is signed. Where the two disagree, the left one wins.

              This is also why Jarvis Local no longer sends analysis with a
              thread: he has no product context to weigh it against, and on the
              first run I asked him to "flag anything urgent", which was me
              outsourcing the one part only I can do. `skill://whatsapp` now
              says so plainly. */}
          <DocumentFrame
            slug="kb-doc/consultancy/falcon/whatsapp/2026-08-20-dan-shika"
            label="19–20 Aug — the full thread, both directions, and all three voice notes transcribed"
            width={880}
            height={1240}
          />
          <DocumentFrame
            slug="kb-doc/consultancy/falcon/whatsapp/2026-08-20-analysis"
            label="My reading — what he is asking us to build, in what order, and what I do not know"
            width={880}
            height={1240}
          />
        </div>
      </SectionBox>
      <Break />
      {/* 17 Aug. Erez asked for the status in writing: where we are, what the
          problem is in detail, and the root cause. Written as a KB page and
          presented here, per the board law — the paper is the one copy.
          It sits directly under Project docs because it is the only OPEN
          exposure on Dan's live box: `falcon_open_file` half-works, and the
          reason is the harness, not our code. The decision it asks for (pull it
          or leave it) is the first thing blocking me. */}
      <SectionBox title="The file problem" layout="custom">
        <div style={{ marginTop: 0 }}>
          <DocumentFrame
            slug="kb-doc/consultancy/falcon/file-memory"
            label="A file is in front of the agent for exactly one turn — status, root cause, and the decision"
            width={1180}
            height={1400}
          />
        </div>
      </SectionBox>
      <Break />
      {/* 18-19 Aug, and it sits HERE because it is the same story continued:
          "The file problem" above ends with the agent able to get a document
          back in front of its eyes. This is what that made possible — it can
          now look at the PICTURES inside a brochure and judge them, and look at
          the document it just built before that document reaches a client.

          It also records the bad idea that was removed. `extract.py` used to
          decide which of Dan's photographs a client would ever see, with five
          measured thresholds and a cap of eight, and the model never saw one of
          them: 100 images passed every filter, 40 were kept, 60 were discarded
          and never written to disk. Erez: "that is a disaster, completely."

          Written in the KB and presented here, per the board law. ADDED, not
          replacing anything — the file-memory page above is still the honest
          account of where that thread started. */}
      <SectionBox title="Pictures & documents — the agent judges, not the arithmetic" layout="custom">
        <div style={{ marginTop: 0 }}>
          <DocumentFrame
            slug="kb-doc/consultancy/falcon/pictures-and-documents"
            label="What was wrong, how it works now, what it cost to get wrong, and what is still unproven"
            width={1180}
            height={1400}
          />
        </div>
      </SectionBox>
      <Break />
      <SectionBox title="The session roll — why it deadlocks, and the fix" layout="custom">
        <div style={{ marginTop: 0 }}>
          <FalconSessionRoll />
        </div>
      </SectionBox>
      {/* 15 Aug. The step lists below are accurate and Erez still could not follow
          them — six explanations in buffers, queued turns and response ids, and he
          asked for a picture. These two are that picture: the same two lists with
          the machinery removed, in the words a client would use. They are rendered
          deterministically (HTML → PNG) rather than generated: Workers AI models
          draw a convincing diagram in which every label is gibberish — tested, and
          the output read "Where stare fow the lste poncess". A diagram that has to
          be READ cannot come from a diffusion model. */}
      {/* Added after Erez read the second picture and killed the design in it.
          "Switch first, summarise second" buys determinism by reordering — and
          the order IS the product: the new conversation is HYDRATED FROM the
          summary, so creating it first gives a conversation that starts blind.
          His words: "if not, the agent will not remember what just happened."
          This third picture is his sequence, and the gate between 4 and 5 is the
          requirement. Determinism has to be found INSIDE that order. */}
      <SectionBox title="The roll — the sequence we want" layout="custom">
        <div style={{ marginTop: 0 }}>
          <img
            src={`${ROLL_BASE}/17dee93c-a4a1-4243-94c1-6b2907c1ad76-roll-sequence-v2.png?v=2`}
            alt="The sequence: save, gate, then switch and hydrate"
            width={1420}
            decoding="async"
            style={{ width: 1420, height: "auto", borderRadius: 14, border: "1px solid #E3E1DD" }}
          />
        </div>
      </SectionBox>
      {/* The product sequence above says WHAT must happen. This pair says HOW,
          in omnigent's own calls, side by side so the delta is visible without
          reading either one twice. Steps 1-6 and 12-14 are byte-identical
          between them; the entire change is four steps in one function. */}
      {/* CLEANED 15 Aug, on Erez's word. Five roll sections had accumulated in
          one day — two plain-language pictures, a now/should pair, and three
          presented KB pages — and the clutter was itself the problem: he could
          not tell which one he was supposed to believe. Two remain: WHAT must
          happen (his product sequence) and HOW we build it (the spec). The KB
          rows are untouched at kb-doc/consultancy/falcon/roll-sequence,
          roll-the-right-way and roll-loop-incident — removed from the board,
          not deleted from the brain. */}
      {/* v2, 15 Aug: re-validated line by line against the code and against
          omnigent's own source on the box, and five steps were wrong. The two
          that would have shipped the bug straight back: step 10 never said
          WHICH door writes the visible line (on `_say` it starts a turn and
          buffers the handover again — the 4m35s defect exactly), and deleting
          `_REASK` removed the only backstop for a falcon-app restart mid-wait.
          The idle signal itself got STRONGER: verified in the box's own
          openapi.json, and the detail route is the only safe one of the two —
          it asks the runner on a cache miss rather than synthesizing an idle.
          Source HTML kept this time at agents/otto/falcon-diagrams/. */}
      <SectionBox title="Session switching logic" layout="custom">
        <div style={{ marginTop: 0 }}>
          <img
            src={`${ROLL_BASE}/session-switching-logic-v2.png?v=2`}
            alt="Session switching logic: every line we change, in order"
            width={1460}
            decoding="async"
            style={{ width: 1460, height: "auto", borderRadius: 14, border: "1px solid #E3E1DD" }}
          />
        </div>
      </SectionBox>
      <Break />
      <SectionBox title="Discovery" layout="custom">
        <div style={{ marginTop: 0 }}>
          <PresentationFrame
            videoSrc="https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/pitch-doc/consultancy/falcon/first-meeting/film.mp4"
            poster="https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/pitch-doc/consultancy/falcon/first-meeting/poster.jpg"
            slidesBase="https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/pitch-doc/consultancy/falcon/first-meeting/slides"
            slideCount={13}
            pdf="https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/pitch-doc/consultancy/falcon/first-meeting/falcon-first-meeting.pdf"
            label="Falcon · discovery, 23 July 2026 · 7:13"
          />
        </div>
      </SectionBox>
      {/* THE TWELVE USE CASES — one presentation, not twelve boxes.
          Erez, 2026-07-25: bring them all into one pitch deck and delete the
          scattered ones. They were five SectionBoxes of hand-built flow cards
          (board/falcon/TelegramFlows.tsx, now deleted); they are now a narrated
          film + click-through slides + a PDF, all generated from ONE
          page_content row (pitch-doc/consultancy/falcon/use-cases) so the deck,
          the film and the handout cannot drift. Each bubble animates onto the
          word that introduces it, detected from the audio (skill://remotion §06b).
          Video project: agents/otto/falcon-usecases-video.
          Sits beside Discovery on purpose: those two ARE the engagement — where
          it came from and what it becomes. Everything below is supporting state. */}
      <SectionBox title="Product" layout="custom">
        <div style={{ marginTop: 0 }}>
          <PresentationFrame
            videoSrc="https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/pitch-doc/consultancy/falcon/use-cases/film.mp4"
            poster="https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/pitch-doc/consultancy/falcon/use-cases/poster.jpg"
            slidesBase="https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/pitch-doc/consultancy/falcon/use-cases/slides"
            slideCount={14}
            pdf="https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/pitch-doc/consultancy/falcon/use-cases/falcon-use-cases.pdf"
            label="Falcon · the twelve use cases · 7:52"
            caption="Everything Dan can do with the Falcon agent, written as the conversations he will actually have rather than as a feature list. Hebrew on purpose — Hebrew voice capture is the thing that decides this build. Property photos are his own."
          />
        </div>
      </SectionBox>

      {/* THE ARCHITECTURE REVIEW — Erez asked whether reusing the Boiling Offers
          architecture is also a chance to re-evaluate how we implemented it. So
          this is a REVIEW read from the actual code (agents/atlas/tailormind,
          4,461 lines) rather than written from the handover notes: what the
          spine gets right, the three gaps Falcon exposes, and the build order.
          Video project: agents/otto/falcon-arch-video. */}
      <SectionBox title="Architecture" layout="custom">
        <div style={{ marginTop: 0 }}>
          <PresentationFrame
            videoSrc="https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/pitch-doc/consultancy/falcon/architecture/film.mp4"
            poster="https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/pitch-doc/consultancy/falcon/architecture/poster.jpg"
            slidesBase="https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/pitch-doc/consultancy/falcon/architecture/slides"
            slideCount={8}
            pdf="https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/pitch-doc/consultancy/falcon/architecture/falcon-architecture.pdf"
            label="Falcon · architecture · 4:57"
            caption="Falcon is a brain, and everything else is a door onto it — that is the whole architecture. Unlike Boiling Offers there is no third-party system holding the data; we build it, so the data IS the product. Four system diagrams: the brain with its three doors (Telegram, the console, and an MCP so it is reachable from ChatGPT), how a voice note becomes a record you can trust, the one write path both the agent and a human pass through, and where it runs."
          />
        </div>
      </SectionBox>
      <Break />
      {/* ROW 2 */}
      {/* What EXISTS as of the Atlas handover, plus the two gaps Otto found
          reading it against discovery. Deep detail: kb-doc/consultancy/falcon/infrastructure. */}
      <SectionBox title="Build state" layout="custom"><div style={{ marginTop: 0 }}><Grid cards={BUILD} cols={2} width={520} /></div></SectionBox>
      {/* THE PLATFORM. Erez, 2026-07-26: "what we're building now is the
          Tailormind platform — a template that we start with and then we can do
          it for each agent. This is a big deal."
          It sits on the FALCON board because Falcon is the first client built
          on it, but nothing in the diagram is Falcon-specific except the domain
          row. It is drawn from the CODE that exists (agents/otto/falcon +
          agents/atlas/tailormind), not from a plan — hence the live / dormant /
          to-build dots, which are the honest part.
          NOTE: the shared Architecture board (/board) is Atlas's file and this
          belongs there too — that is a handover, not something to take. */}
      <SectionBox title="The platform" layout="custom">
        <div style={{ marginTop: 0 }}><PlatformArchitecture /></div>
      </SectionBox>
      {/* INVENTORY, AS DAN ASKED FOR IT. He was drafting an email to a tenant
          and wanted the sub-500 stock as plain text he could copy, in his own
          format — building and address, size, floor, price, management fee.
          It is a KB page presented here rather than cards, because that is the
          only legal way to put prose on a board and because the list is pulled
          from `properties` and will go stale: regenerate the page, the board
          follows. The warning about nothing being confirmed lives ON the page,
          not in this file, so it travels with the list wherever it is read. */}
      <SectionBox title="Inventory — up to 500 m²" layout="custom">
        <div style={{ marginTop: 0 }}>
          <DocumentFrame
            slug="kb-doc/consultancy/falcon/inventory-under-500"
            label="14 properties, smallest to largest — for the client email"
            width={900}
            height={1400}
          />
        </div>
      </SectionBox>
      {/* THE VOICE, playable here. Erez can now hear the Hebrew well enough to
          give real feedback on it, so the dial belongs on the board rather than
          in a folder of files he has to go find. The sentence is identical in
          every clip; only the mix moves. */}
      {/* ── ROW BREAK ──────────────────────────────────────────────────────
          SectionGrid is a flex-wrap, so it packs boxes by width. That is right
          for card boxes but not for the site wall, which is one very long row
          and must land on a line of its own. A full-width zero-height flex item
          forces the break — the same technique the Marketing board uses to pin
          its rows (skill://board). Without it the wall drags a neighbour onto
          its line and the whole board goes crooked. */}
      <SectionBox title="Voice" layout="custom">
        <div style={{ marginTop: 0, display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ fontFamily: FONT, fontSize: 15, lineHeight: 1.55, color: T.ink2, maxWidth: 1046 }}>
            The Hebrew voice is a <b>blend</b>, not a single voice — Kokoro voices are style vectors, so two of them
            average into one that does not exist in the model. <b>“More echo” is a dial.</b> Same sentence in every clip,
            a real Falcon reply rather than a test phrase, encoded as Telegram Opus so this is what Dan will actually hear.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 510px)", gap: 26 }}>
            {VOICE_CLIPS.map((c) => <VoiceCard key={c.file} c={c} width={510} />)}
          </div>
          <div style={{ fontFamily: MONO, fontSize: 12.5, lineHeight: 1.6, color: T.faint, maxWidth: 1046 }}>
            Other knobs: speed, and the R. Dave forced an English-style R because the authentic uvular Israeli one
            garbles exactly the words this product cannot lose — דירות → דהות, ברחוב → ביחוב, הרצל → הרץ.
            Not recommended for client work, but available. Generator: agents/otto/voice-samples/voice_mixes.py
          </div>
        </div>
      </SectionBox>
      {/* MEETING 2 — the specification session. Sits in row 2, beside the build
          state, because it is the freshest statement of WHAT we are building
          and it should be read before the how. Deep read: the KB page. */}
      <SectionBox title="Meeting 2" layout="custom">
        <div style={{ marginTop: 0 }}><MeetingTwo /></div>
      </SectionBox>
      {/* MEETING 3 — the working session, 10 August. Two KB pages presented,
          per the board law: the distilled read (eleven bugs, eleven asks, the
          four-store model Erez introduced in the room) and the full English
          translation beside it. Written in the KB, shown here — no card wall.
          This one earns its place next to Meeting 2 because it is the first
          session where the product failed IN FRONT OF the client, which is
          where most of its value is. */}
      <SectionBox title="Meeting 3" layout="custom">
        <div style={{ marginTop: 0, display: "flex", gap: 26, alignItems: "flex-start" }}>
          <DocumentFrame slug="kb-doc/consultancy/falcon/meeting-03" label="The working session — bugs, asks, decisions" width={820} height={1140} />
          <DocumentFrame slug="kb-doc/consultancy/falcon/meeting-03-transcript" label="Full transcript, translated" width={820} height={1140} />
        </div>
      </SectionBox>
      <Break />
      {/* ROW 3 — THE EMAIL TEMPLATE, on a line of its own because the skeleton
          panel is tall and would otherwise drag a neighbour onto its line.
          Dan's FIRST real request of the system (3 August): two voice notes
          asking for an email he can copy, paste and fill for a live client.
          Copyable text with a working copy button, because he pastes it into
          his mail client — same device as the meeting-2 recap. Every figure in
          it is his own, from the 29 July transcript. */}
      <SectionBox title="Email template" layout="custom">
        <div style={{ marginTop: 0 }}><EmailTemplate /></div>
      </SectionBox>
      <Break />
      {/* ROW 4 — THE COMPETITOR WALL, on a line of its own. Erez, 2026-07-29:
          one row, all of them next to each other, as the third row of the
          board. Fifteen Israeli sites and the five US majors: the boutique
          office-brokerage shops Dan competes with, the marketplaces he is
          measured against (Yad2 above all — "my own Yad2" was his own phrase
          in discovery), and the global brands' Hebrew face. Feeds requirement
          4: Falcon has no website and no brand today. */}
      <SectionBox title="The competition" layout="custom">
        <div style={{ marginTop: 0 }}><SiteWall /></div>
      </SectionBox>
      <Break />
      {/* Direction settled + the correction that closed the tenant question. */}
      <SectionBox title="Direction" layout="custom"><div style={{ marginTop: 0 }}><Grid cards={DIRECTION} cols={2} width={520} /></div></SectionBox>
      {/* Live state: what is blocking RIGHT NOW. Full context is in document 04. */}
      <SectionBox title="Decisions" layout="custom"><div style={{ marginTop: 0 }}><Grid cards={DECISIONS} cols={2} width={520} /></div></SectionBox>
    </SectionGrid>
  );
}
const nodeTypes: NodeTypes = { grid: GridNode as unknown as NodeTypes[string] };

export function FalconBoardPage() {
  const nodes = useMemo<Node[]>(() => [{ id: "grid", type: "grid", position: { x: 0, y: 0 }, draggable: false, data: {} }], []);
  return (
    <div style={{ position: "absolute", inset: 0 }}>
      <BoardCanvas nodes={nodes} nodeTypes={nodeTypes} fitViewPadding={0.1} />
    </div>
  );
}
